import _ from 'underscore';
import { STEP_PATHS, FLOWS, isAudienceCampaign, isAudienceCampaignEdit, isAudienceAdGroupEdit, isAudienceAdGroupCreation, shouldEnableBidAdjustment, isCTVCampaign, isDealCampaign } from './step-constants';

const addSummaryStep = ({ steps, permissions }) => {
  if (permissions.dynamic.DisplayPlusSummaryPage) {
    steps.push(STEP_PATHS.SUMMARY);
    return steps;
  }

  return steps;
};

export const getFlow = ({ flow, permissions }) => {
  if (flow === FLOWS.VIDEO_CAMPAIGNS || flow === FLOWS.CTV_CAMPAIGNS || flow === FLOWS.DEAL_CAMPAIGNS) {
    const steps = [
      STEP_PATHS.VIDEO_AD,
      STEP_PATHS.AD_GROUP,
      STEP_PATHS.CAMPAIGN_CREATION,
    ];

    return addSummaryStep({ steps, permissions });
  }

  if (flow === FLOWS.AUDIENCE_CAMPAIGNS) {
    const steps = [
      STEP_PATHS.NATIVE_AD,
      STEP_PATHS.AD_GROUP,
      STEP_PATHS.CAMPAIGN_CREATION,
    ];

    return addSummaryStep({ steps, permissions });
  }

  if (flow === FLOWS.MULTI_AUDIENCE_CAMPAIGNS) {
    const steps = [
      STEP_PATHS.MULTI_AD_MANAGER,
      STEP_PATHS.MULTI_NATIVE_AD,
      STEP_PATHS.AD_GROUP,
      STEP_PATHS.CAMPAIGN_CREATION,
    ];

    return addSummaryStep({ steps, permissions });
  }

  if (flow === FLOWS.DISPLAY_CAMPAIGNS) {
    const steps = [
      STEP_PATHS.DISPLAY_AD,
      STEP_PATHS.AD_GROUP,
      STEP_PATHS.CAMPAIGN_CREATION,
    ];

    return addSummaryStep({ steps, permissions });
  }

  if (flow === FLOWS.AUDIENCE_CAMPAIGN_EDIT || flow === FLOWS.AUDIENCE_CAMPAIGN_CTV_EDIT || flow === FLOWS.AUDIENCE_CAMPAIGN_DEAL_EDIT) {
    return [
      STEP_PATHS.CAMPAIGN_EDIT,
    ];
  }

  if (flow === FLOWS.AUDIENCE_AD_GROUP_EDIT || flow === FLOWS.AUDIENCE_AD_GROUP_CTV_EDIT || flow === FLOWS.AUDIENCE_AD_GROUP_DEAL_EDIT) {
    return [
      STEP_PATHS.AD_GROUP,
    ];
  }

  if (flow === FLOWS.AUDIENCE_AD_GROUP_CREATION) {
    return [
      STEP_PATHS.AD_GROUP,
      STEP_PATHS.NATIVE_AD,
    ];
  }

  if (flow === FLOWS.AUDIENCE_AD_GROUP_CTV_CREATION || flow === FLOWS.AUDIENCE_AD_GROUP_DEAL_CREATION) {
    return [
      STEP_PATHS.AD_GROUP,
      STEP_PATHS.VIDEO_AD,
    ];
  }

  if (flow === FLOWS.AUDIENCE_AD_GROUP_MULTI_AD_CREATION) {
    return [
      STEP_PATHS.AD_GROUP,
      STEP_PATHS.MULTI_NATIVE_AD,
      STEP_PATHS.MULTI_AD_MANAGER,
    ];
  }

  // return video by default
  return [
    STEP_PATHS.VIDEO_AD,
    STEP_PATHS.AD_GROUP,
    STEP_PATHS.CAMPAIGN_CREATION,
  ];
};

export const getAdGroupPanels = (flow, serverContext = {}) => {
  const {
    DynamicPermissions: {
      XandrDeviceTarget,
      DisplayPlusAmplify,
      Enable1PPlacementTargeting,
      EnableMSANDisplayCampaigns,
      EnableMSANVideoCampaigns,
      DeviceTargetingForDisplay,
      EnableKeywordCustomSegments,
      MultiAdFormat,
      InMarketAudienceRedesign,
      ContentTargeting,
      PremiumStreaming,
      GenreTargets,
    },
    Permissions,
  } = serverContext;

  if (isCTVCampaign(flow) || isDealCampaign(flow)) {
    const options = {
      basicOptions: [
        'ad-group-name',
        'bid-value',
        'location-target',
        'ad-schedule',
      ],
      advancedOptions: [],
    };

    if (GenreTargets) {
      options.basicOptions.push('genre-targets');
    }

    if (isCTVCampaign(flow)) {
      options.basicOptions.push('netflix-tc', 'gender-target', 'age-target');
      options.advancedOptions.push('audience-target');

      if (InMarketAudienceRedesign) {
        options.advancedOptions.push('interests-target');
      }

      if (PremiumStreaming) {
        options.advancedOptions.push('device-target');
      }
    }

    return options;
  }

  if (isAudienceCampaign(flow) || isAudienceAdGroupCreation(flow)) {
    const is1PPlacementEnabled = Enable1PPlacementTargeting && !Permissions.IsAmplifyEnabled;
    const options = {
      basicOptions: [
        'ad-group-name',
        isAudienceAdGroupCreation(flow) && 'bid-value',
        isAudienceAdGroupCreation(flow) && 'start-end-date',
        'location-target',
        'gender-target',
        'age-target',
        'predictive-targeting',
      ],
      advancedOptions: Permissions.IsDomesticSimplifiedChineseEnabled ?
        [
          'audience-target',
          'predictive-targeting-control',
        ] :
        _.compact([
          'audience-target',
          MultiAdFormat && 'device-target',
          'company-target',
          'industry-target',
          'job-target',
          is1PPlacementEnabled && !ContentTargeting && 'placements-1P-target',
          'placements-target',
          isAudienceAdGroupCreation(flow) && 'ad-schedule',
          'predictive-targeting-control',
        ]),
    };

    if (Permissions.IsAmplifyEnabled && DisplayPlusAmplify) {
      options.basicOptions.push('placement-target');
      options.basicOptions.push('contextual-target');
    }

    if (EnableKeywordCustomSegments) {
      options.advancedOptions.push('custom-segments');
    }

    if (InMarketAudienceRedesign) {
      options.advancedOptions.push('interests-target');
    }

    if (ContentTargeting) {
      options.advancedOptions.push('content-targeting-placements');
    }

    return options;
  } else if (isAudienceAdGroupEdit(flow)) {
    const is1PPlacementEnabled = Enable1PPlacementTargeting && !Permissions.IsAmplifyEnabled && !Permissions.IsDomesticSimplifiedChineseEnabled;
    const options = {
      basicOptions: [
        'ad-group-name',
        'start-end-date',
        'bid-value',
        'location-target',
        'gender-target',
        'age-target',
        'predictive-targeting',
      ],
      advancedOptions: [
        'audience-target',
        'device-target',
        'company-target',
        'industry-target',
        'job-target',
        'advanced-url-options',
        'ad-rotation',
        'ad-schedule',
        'web-exclusions',
        'predictive-targeting-control',
      ],
    };

    if (EnableKeywordCustomSegments) {
      options.advancedOptions.push('custom-segments');
    }
    if (Permissions.IsAmplifyEnabled && DisplayPlusAmplify) {
      options.basicOptions.push('placement-target');
      options.basicOptions.push('contextual-target');
    }

    if (InMarketAudienceRedesign) {
      options.advancedOptions.push('interests-target');
    }

    if (ContentTargeting) {
      options.advancedOptions.push('content-targeting-placements');
    } else if (is1PPlacementEnabled) {
      options.advancedOptions.push('placements-1P-target');
    }

    return options;
  } else if (flow === FLOWS.DISPLAY_CAMPAIGNS) {
    const options = {
      basicOptions: [
        'ad-group-name',
        'location-target',
        'gender-target',
        'age-target',
      ],
      advancedOptions: [
        'audience-target',
        DeviceTargetingForDisplay && 'device-target',
        'ad-schedule',
      ],
    };

    if (EnableMSANDisplayCampaigns) {
      options.basicOptions.push('predictive-targeting');
      options.advancedOptions.push('company-target');
      options.advancedOptions.push('industry-target');
      options.advancedOptions.push('job-target');
      options.advancedOptions.push('predictive-targeting-control');
    }

    if (EnableKeywordCustomSegments) {
      options.advancedOptions.push('custom-segments');
    }

    if (InMarketAudienceRedesign) {
      options.advancedOptions.push('interests-target');
    }

    if (ContentTargeting) {
      options.advancedOptions.push('content-targeting-placements');
    }

    return options;
  }

  const options = {
    basicOptions: [
      'ad-group-name',
      'location-target',
      'gender-target',
      'age-target',
    ],
    advancedOptions: [
      'audience-target',
      XandrDeviceTarget && 'device-target',
      'ad-schedule',
    ],
  };

  if (EnableMSANVideoCampaigns) {
    options.basicOptions.push('predictive-targeting');
    options.advancedOptions.push('company-target');
    options.advancedOptions.push('industry-target');
    options.advancedOptions.push('job-target');
    options.advancedOptions.push('predictive-targeting-control');
  }

  if (EnableKeywordCustomSegments) {
    options.advancedOptions.push('custom-segments');
  }

  if (InMarketAudienceRedesign) {
    options.advancedOptions.push('interests-target');
  }

  if (ContentTargeting) {
    options.advancedOptions.push('content-targeting-placements');
  }

  return options;
};

export const getCampaignPanels = (flow, serverContext) => {
  const {
    DynamicPermissions: {
      EnableMSANDisplayCampaigns,
      EnableMSANVideoCampaigns,
      AudienceMMACTAOptOut,
      EnableDoubleVerify,
      CampaignLifetimeBudget,
      MaeLifetimeBudgetExclusion,
    },
    Permissions: {
      IsMSANDisclaimerEnabled,
      IsMSANISPOTEnabled,
      IsDomesticSimplifiedChineseEnabled,
      IsAmplifyEnabled,
    },
  } = serverContext;

  const isMSANDisplay = EnableMSANDisplayCampaigns && flow === FLOWS.DISPLAY_CAMPAIGNS;
  const isMSANVideo = EnableMSANVideoCampaigns && flow === FLOWS.VIDEO_CAMPAIGNS;

  const isCampaignLifetimeBudgetEnabled = CampaignLifetimeBudget && !MaeLifetimeBudgetExclusion;

  if (isCTVCampaign(flow) || isDealCampaign(flow)) {
    const options = {
      basicOptions: [
        'campaign-name',
        'bid-and-budget',
      ],
      advancedOptions: [],
    };

    if (IsMSANISPOTEnabled) {
      options.advancedOptions.push('third-party-measurement-settings');
    }

    return options;
  }

  if (flow === FLOWS.AUDIENCE_CAMPAIGN_EDIT) {
    const options = {
      basicOptions: [
        'campaign-name',
        'bid-and-budget',
        isCampaignLifetimeBudgetEnabled && 'start-end-dates',
        'conversions',
        'location-target',
      ],
      advancedOptions: [
        'ad-schedule',
        'device-target',
        'advanced-url-options',
        'web-exclusions',
      ],
    };

    // We do not allow disclaimer for domestic simplified chinese since they do not support pop up disclaimer
    if (!IsDomesticSimplifiedChineseEnabled && IsMSANDisclaimerEnabled) {
      options.basicOptions.push('disclaimer');
    }

    if (AudienceMMACTAOptOut && !serverContext.Permissions.IsAmplifyEnabled) {
      options.basicOptions.push('call-to-action-opt-out');
    }

    if (IsMSANISPOTEnabled || EnableDoubleVerify) {
      options.advancedOptions.push('third-party-measurement-settings');
    }

    if (IsAmplifyEnabled) {
      options.basicOptions.push('campaign-goal');
    }

    return options;
  }

  if (isAudienceCampaign(flow)) {
    const options = {
      basicOptions: [
        'campaign-name',
        'budget-date-settings',
        'bid-and-budget',
        'bid-value',
        'conversions',
        'start-end-dates',
      ],
      advancedOptions: [
        'ad-schedule',
      ],
    };

    if (shouldEnableBidAdjustment(flow, serverContext)) {
      options.advancedOptions.push('bid-adjustment');
    }

    if (!IsDomesticSimplifiedChineseEnabled && IsMSANDisclaimerEnabled) {
      options.basicOptions.push('disclaimer');
    }

    if (IsMSANISPOTEnabled || EnableDoubleVerify) {
      options.advancedOptions.push('third-party-measurement-settings');
    }

    return options;
  } else if (flow === FLOWS.VIDEO_CAMPAIGNS || flow === FLOWS.DISPLAY_CAMPAIGNS) {
    const options = {
      basicOptions: [
        'campaign-name',
        'bid-and-budget',
        'bid-value',
        'conversions',
        (isMSANDisplay || isMSANVideo) && 'start-end-dates',
      ],
      advancedOptions: _.compact([
        (shouldEnableBidAdjustment(flow, serverContext) &&
          (isMSANDisplay || isMSANVideo)) && 'bid-adjustment',
      ]),
    };

    if (IsMSANISPOTEnabled || EnableDoubleVerify) {
      options.advancedOptions.push('third-party-measurement-settings');
    }

    return options;
  }

  return {
    basicOptions: [
      'campaign-name',
      'bid-and-budget',
      'bid-value',
      'start-end-dates',
    ],
    advancedOptions: [],
  };
};

export const getNativeAdPanels = (serverContext) => {
  const panels = {
    basicPanels: [
      'images',
      'business-name',
      'text',
    ],
    advancedPanels: [
      'mobile-final-url',
      'advanced-url-options',
      'impression-tracking-URLs',
      'third-party-measurement',
    ],
  };

  if (serverContext.Permissions.IsAmplifyEnabled && serverContext.DynamicPermissions.DisplayPlusAmplify) {
    panels.basicPanels.push('logos');
    panels.basicPanels.push('hotspot-panel');
    panels.basicPanels.push('portrait-images');

    panels.advancedPanels.push('anchor-ad');
    panels.advancedPanels.push('call-to-action');
  } else if (serverContext.Permissions.IsMSANLogoAndCTAForMMAEnabled) {
    panels.basicPanels.push('logos', 'call-to-action');
  }

  return panels;
};

export const getIsEditScenario = flow => isAudienceCampaignEdit(flow) || isAudienceAdGroupEdit(flow);
